import DeleteIcon from "@assets/icons/delete.svg";
import EyeVisibleIcon from "@assets/icons/eye-visible.svg";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { PatientBase, PatientStatus } from "@domain/patient/patient";
import { AnalyticsProgramClickType, sendAnalyticsEventViewPatientDetail } from "@domain/program/program-analytics";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { MenuButton, MenuItemProps } from "@ui/components/common/menu-button";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { colors } from "@ui/style/colors";
import _ from "lodash";
import React, { useContext, useMemo } from "react";

interface PatientActionButtonProps {
	patient: PatientBase & { status: PatientStatus };
}

export const PatientActionButton = ({ patient }: PatientActionButtonProps): JSX.Element => {
	const { id, analyticsId, status } = patient;
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);
	const { format } = useI18n();

	const items: MenuItemProps[] = useMemo(
		() =>
			_.compact([
				status === PatientStatus.Registered
					? {
							icon: <EyeVisibleIcon />,
							text: format("global.patientOperation.see"),
							onClick: () => {
								navigate(RoutesPath.PatientDetails, {
									route: { patientId: id },
									query: { active: TabPatientDetails.PROGRAMS_TAB },
								});
								sendAnalyticsEventViewPatientDetail(analyticsId, AnalyticsProgramClickType.OPTION_CLICK);
							},
						}
					: {
							destructive: true,
							text: format("global.patientOperation.cancelInvitation"),
							icon: <DeleteIcon />,
							onClick: () => {
								dialogContext?.openModal(ModalId.CancelPatientInvitation, { patient });
							},
						},
			]),
		[analyticsId, dialogContext, format, id, navigate, patient, status],
	);

	return <ActionButton menuId={id} menuItems={items} />;
};

const ActionButton = styled(MenuButton)`
	.menu-button {
		background-color: ${colors.transparent};

		&.hover {
			background-color: ${colors.grey10};
		}
	}
`;
